import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Button from "shared/components/FormElements/Button";
import { CUSTOMER_REQUEST_OPEN_STATUSES, CUSTOMER_REQUEST_STATUSES } from "@routebot/constants";
import useCustomerPortalApi from "./hooks/useCustomerPortalApi";
import CustomerRequestDetail from "./CustomerRequestDetail";
import { asId } from "./ids";
import { EXTRA_SERVICE_TYPE, formatExtraServiceDate } from "./extraService";
import "./CustomerPortal.css";

const formatWhen = (value, language) => {
  if (!value) return "";
  try {
    return new Date(value).toLocaleString(language?.startsWith("en") ? "en-GB" : "tr-TR", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (err) {
    return "";
  }
};

const CustomerRequestInbox = ({ variant = "contact" }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id: selectedId } = useParams();
  const { isLoading, error, listRequests, listSchools } = useCustomerPortalApi();
  const [requests, setRequests] = useState([]);
  const [schools, setSchools] = useState([]);
  const [status, setStatus] = useState(variant === "operator" ? "__open" : "");
  const [school, setSchool] = useState("");
  const [query, setQuery] = useState("");
  const isOperator = variant === "operator";
  const isContact = variant === "contact";
  const detailBase = isOperator ? "/customer-requests" : "/portal/requests";

  const load = useCallback(async () => {
    try {
      const [rows, schoolRows] = await Promise.all([
        listRequests({
          status: status && status !== "__open" ? status : undefined,
          open: status === "__open" ? true : undefined,
          school: school || undefined,
        }),
        listSchools(),
      ]);
      setRequests(rows);
      setSchools(schoolRows);
    } catch (err) {
      setRequests([]);
    }
  }, [listRequests, listSchools, status, school]);

  useEffect(() => {
    load();
  }, [load]);

  const schoolName = useMemo(() => {
    const map = {};
    schools.forEach((item) => {
      map[item.id] = item.name;
    });
    return map;
  }, [schools]);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return requests;
    return requests.filter((row) => {
      const schoolId = row.school?.id || row.school;
      const hay = [
        row.title,
        row.schoolName,
        schoolName[schoolId],
        row.relatedStudentName,
        row.relatedStudentIdentifier,
        row.createdByName,
        row.extraService?.origin,
        row.extraService?.destination,
        row.extraService?.departureTime,
        row.extraService?.returnTime,
        row.extraService?.headcount,
        row.extraService?.date,
        formatExtraServiceDate(row.extraService?.date),
        t(`customerPortal.types.${row.type}`, { defaultValue: row.type }),
        t(`customerPortal.statuses.${row.status}`, { defaultValue: row.status }),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(needle);
    });
  }, [requests, query, schoolName, t]);

  const openRow = (id) => {
    if (!id) return;
    navigate(`${detailBase}/${id}`, { replace: Boolean(selectedId) });
  };

  const applyDocToList = useCallback(
    (doc) => {
      if (!doc) return;
      const docId = asId(doc.id || doc._id);
      if (!docId) return;
      setRequests((prev) => {
        const idx = prev.findIndex((row) => asId(row.id || row._id) === docId);
        if (idx === -1) return prev;
        if (status === "__open" && !CUSTOMER_REQUEST_OPEN_STATUSES.includes(doc.status)) {
          return prev.filter((_, index) => index !== idx);
        }
        const next = [...prev];
        next[idx] = {
          ...next[idx],
          status: doc.status,
          title: doc.title || next[idx].title,
          attachmentCount: Array.isArray(doc.attachments) ? doc.attachments.length : next[idx].attachmentCount,
        };
        return next;
      });
    },
    [status],
  );

  return (
    <div className={`cp cp-inbox${selectedId ? " cp-inbox--selected" : ""}`}>
      <div className="cp-inbox-body">
        <aside className="cp-inbox-rail" aria-label={isOperator ? t("customerPortal.operatorTitle") : t("customerPortal.contactTitle")}>
          <div className="cp-inbox-rail-head">
            <div className="cp-inbox-rail-title">
              <h1>{isOperator ? t("customerPortal.operatorTitle") : t("customerPortal.contactTitle")}</h1>
              {isContact && (
                <Button to="/portal/requests/new" containerStyle={{ marginTop: 0 }} size="small">
                  {t("customerPortal.newRequest")}
                </Button>
              )}
            </div>
            {error && <div className="cp-error">{error}</div>}
            <div className="cp-filters">
              <select value={status} onChange={(event) => setStatus(event.target.value)} aria-label={t("customerPortal.status")}>
                <option value="">{t("customerPortal.allStatuses")}</option>
                {isOperator && <option value="__open">{t("customerPortal.openQueue")}</option>}
                {Object.values(CUSTOMER_REQUEST_STATUSES).map((value) => (
                  <option key={value} value={value}>
                    {t(`customerPortal.statuses.${value}`)}
                  </option>
                ))}
              </select>
              {schools.length > 1 && (
                <select value={school} onChange={(event) => setSchool(event.target.value)} aria-label={t("customerPortal.school")}>
                  <option value="">{t("customerPortal.allSchools")}</option>
                  {schools.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
              )}
              <input
                type="search"
                className="cp-search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={t("customerPortal.searchPlaceholder")}
                aria-label={t("customerPortal.searchPlaceholder")}
              />
            </div>
          </div>

          {isLoading && requests.length === 0 ? (
            <p className="cp-muted cp-inbox-pane-msg">{t("customerPortal.loading")}</p>
          ) : requests.length === 0 ? (
            <div className="cp-empty cp-inbox-empty-list">
              <strong>{t("customerPortal.empty")}</strong>
              {isContact && <p className="cp-muted">{t("customerPortal.emptyHint")}</p>}
            </div>
          ) : visible.length === 0 ? (
            <div className="cp-empty cp-inbox-empty-list">{t("customerPortal.emptyFiltered")}</div>
          ) : (
            <ul className="cp-ticket-list">
              {visible.map((row) => {
                const id = row.id || row._id;
                const active = asId(id) === asId(selectedId);
                const schoolId = row.school?.id || row.school;
                const meta = [
                  schoolName[schoolId] || row.schoolName,
                  t(`customerPortal.types.${row.type}`, { defaultValue: row.type }),
                  formatWhen(row.createdAt, i18n.language),
                ].filter(Boolean);
                const tripSub =
                  row.type === EXTRA_SERVICE_TYPE && row.extraService
                    ? [
                        [row.extraService.departureTime, row.extraService.returnTime].filter(Boolean).join(" – "),
                        row.extraService.headcount,
                      ]
                        .filter(Boolean)
                        .join(" · ")
                    : "";
                return (
                  <li key={id}>
                    <button
                      type="button"
                      className={`cp-ticket${active ? " cp-ticket--active" : ""}`}
                      onClick={() => openRow(id)}
                      aria-current={active ? "page" : undefined}
                    >
                      <div className="cp-ticket-main">
                        <div className="cp-ticket-title-row">
                          <span className="cp-ticket-title">{row.title}</span>
                          <span className={`cp-badge cp-badge--${row.status}`}>{t(`customerPortal.statuses.${row.status}`)}</span>
                        </div>
                        <div className="cp-ticket-meta">{meta.join(" · ")}</div>
                        {tripSub && <div className="cp-ticket-sub">{tripSub}</div>}
                        {(row.relatedStudentName || (isOperator && row.createdByName)) && (
                          <div className="cp-ticket-sub">
                            {row.relatedStudentName
                              ? `${row.relatedStudentName}${row.relatedStudentIdentifier ? ` · ${row.relatedStudentIdentifier}` : ""}`
                              : ""}
                            {isOperator && row.createdByName ? `${row.relatedStudentName ? " · " : ""}${row.createdByName}` : ""}
                          </div>
                        )}
                      </div>
                      {row.attachmentCount > 0 && (
                        <span className="cp-ticket-files" title={t("customerPortal.attachments")}>
                          {t("customerPortal.filesCount", { count: row.attachmentCount })}
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </aside>

        {selectedId ? (
          <CustomerRequestDetail
            key={selectedId}
            variant={variant}
            requestId={selectedId}
            onChanged={applyDocToList}
            onDeleted={(id) => {
              setRequests((prev) => prev.filter((row) => asId(row.id || row._id) !== asId(id)));
              navigate(detailBase, { replace: true });
            }}
          />
        ) : (
          <div className="cp-inbox-placeholder">
            <p className="cp-inbox-placeholder-title">{t("customerPortal.pickRequest")}</p>
            <p className="cp-muted">{t("customerPortal.pickRequestHint")}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerRequestInbox;
